import { getAllAssets, getPageDatabyIdandLanguage } from '../util/api';
import { SUPPORTED_LANGUAGES } from '../util/constants';
import { denormalizeAssetData } from '../util/data';
import { storableError } from '../util/errors';
import * as log from '../util/log';

// Pick paths from entries of appCdnAssets config (in configDefault.js)
const pickHostedConfigPaths = (assetEntries, excludeAssetNames) => {
  // E.g. allPaths = ['any/foo.json', 'any/bar.json']
  return assetEntries.reduce((pickedPaths, [name, path]) => {
    if (excludeAssetNames.includes(name)) {
      return pickedPaths;
    }
    return [...pickedPaths, path];
  }, []);
};
const getFirstAssetData = response => response?.data?.data[0]?.attributes?.data;
const getMultiAssetData = response => response?.data?.data;
const getMultiAssetIncluded = response => response?.data?.included;
const findJSONAsset = (assets, absolutePath) =>
  assets.find(a => a.type === 'jsonAsset' && a.attributes.assetPath === absolutePath);
const getAbsolutePath = path => (path.charAt(0) !== '/' ? `/${path}` : path);

const sanitizeBlocks = blocks => {
  if (!Array.isArray(blocks)) {
    return blocks;
  }
  return blocks.filter(block => block && typeof block === 'object');
};

const sanitizeSections = sections => {
  if (!Array.isArray(sections)) {
    return sections;
  }
  return sections
    .filter(section => section && typeof section === 'object')
    .map(section => ({
      ...section,
      blocks: sanitizeBlocks(section.blocks),
    }));
};

const sanitizePageAssetData = data => {
  if (!data || typeof data !== 'object') {
    return data;
  }
  return {
    ...data,
    sections: sanitizeSections(data.sections),
  };
};

const getGoogleAnalyticsId = (configAssets, path) => {
  if (!configAssets || !path) {
    return null;
  }
  const configAssetsData = getMultiAssetData(configAssets);
  const jsonAsset = findJSONAsset(configAssetsData, getAbsolutePath(path));
  const { enabled, measurementId } = jsonAsset?.attributes?.data?.googleAnalytics || {};
  return enabled ? measurementId : null;
};

// ================ Action types ================ //

export const ASSETS_REQUEST = 'app/assets/REQUEST';
export const ASSETS_SUCCESS = 'app/assets/SUCCESS';
export const ASSETS_ERROR = 'app/assets/ERROR';

export const PAGE_ASSETS_REQUEST = 'app/assets/PAGE_ASSETS_REQUEST';
export const PAGE_ASSETS_SUCCESS = 'app/assets/PAGE_ASSETS_SUCCESS';
export const PAGE_ASSETS_ERROR = 'app/assets/PAGE_ASSETS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // List of app-wide assets that should be fetched and their path in Asset API.
  // appAssets: { assetName: 'path/to/asset.json' }
  appAssets: {},
  pageAssetsData: null,
  currentPageAssets: [],
  // Current version of the saved asset.
  // Typically, the version that is returned by the "latest" alias.
  version: null,
  inProgress: false,
  error: null,
};

export default function assetReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ASSETS_REQUEST:
      return { ...state, inProgress: true, error: null };
    case ASSETS_SUCCESS:
      return {
        ...state,
        appAssets: payload.assets,
        version: state.version || payload.version,
        googleAnalyticsId: payload.googleAnalyticsId,
        inProgress: false,
      };
    case ASSETS_ERROR:
      return { ...state, inProgress: false, error: payload };

    case PAGE_ASSETS_REQUEST:
      return { ...state, currentPageAssets: payload, inProgress: true, error: null };
    case PAGE_ASSETS_SUCCESS:
      return { ...state, pageAssetsData: payload, inProgress: false };
    case PAGE_ASSETS_ERROR:
      return { ...state, inProgress: false, error: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const appAssetsRequested = () => ({ type: ASSETS_REQUEST });
export const appAssetsSuccess = (assets, version, googleAnalyticsId) => ({
  type: ASSETS_SUCCESS,
  payload: { assets, version, googleAnalyticsId },
});
export const appAssetsError = error => ({
  type: ASSETS_ERROR,
  payload: error,
});

export const pageAssetsRequested = assetKeys => ({ type: PAGE_ASSETS_REQUEST, payload: assetKeys });
export const pageAssetsSuccess = assets => ({ type: PAGE_ASSETS_SUCCESS, payload: assets });
export const pageAssetsError = error => ({
  type: PAGE_ASSETS_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const fetchAppAssets = (assets, version) => async (dispatch, getState, sdk) => {
  dispatch(appAssetsRequested());

  // App-wide assets include 2 content assets: translations for microcopy and footer
  const translationsPath = assets.translations;
  const footerPath = assets.footer;

  // The rest of the assets are considered as configurations
  const assetEntries = Object.entries(assets);
  const nonConfigAssets = ['translations', 'footer'];
  const configPaths = pickHostedConfigPaths(assetEntries, nonConfigAssets);

  // Filter out top-bar.json as coming from console
  const filteredPaths = configPaths.filter(path => path !== '/content/top-bar.json');

  // If version is given fetch assets by the version,
  // otherwise default to "latest" alias
  const fetchAssets = paths =>
    version
      ? sdk.assetsByVersion({ paths, version })
      : sdk.assetsByAlias({ paths, alias: 'latest' });

  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';
  // Extract locale from URL (e.g., /fr/page), default to browser preference
  const fromUrl = SUPPORTED_LANGUAGES.find(
    code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
  );

  const lang = fromUrl || 'en';

  // OPTIMIZATION: Execute all asset fetches in parallel for better performance
  const separateAssetFetches = [
    // This is a big file, better fetch it alone.
    // Then browser cache also comes into play.
    fetchAssets([translationsPath]),
    // Fetch all assets (footer, topbar) in a single API call
    getAllAssets(lang),
    // App configs
    fetchAssets(filteredPaths),
  ];

  // OPTIMIZATION: Use Promise.allSettled for better error handling and performance
  return Promise.allSettled(separateAssetFetches)
    .then((results) => {
      // OPTIMIZATION: Extract results with better error handling
      const [translationResult, allAssetsResult, configResult] = results;
      // Handle failed requests gracefully
      if (translationResult.status === 'rejected') {
        console.warn('Failed to fetch translations:', translationResult.reason);
      }
      if (allAssetsResult.status === 'rejected') {
        console.warn('Failed to fetch assets:', allAssetsResult.reason);
      }
      if (configResult.status === 'rejected') {
        console.warn('Failed to fetch configs:', configResult.reason);
      }

      // Extract successful results
      const translationAsset = translationResult.status === 'fulfilled' ? translationResult.value : null;
      const allAssets = allAssetsResult.status === 'fulfilled' ? allAssetsResult.value.data : null;
      const configAssets = configResult.status === 'fulfilled' ? configResult.value : null;

      // OPTIMIZATION: Early return if critical assets failed
      if (!translationAsset || !configAssets) {
        throw new Error('Critical assets failed to load');
      }

      const getVersionHash = response => response?.data?.meta?.version;
      const versionInTranslationsCall = getVersionHash(translationAsset);

      // Extract footer and topbar from the unified response
      const footerAsset = allAssets?.footer && allAssets.footer.data ? { data: allAssets.footer.data } : null;
      const topbarResponse = allAssets?.topbar && allAssets.topbar.data ? { data: allAssets.topbar.data } : null;

      const versionInFooterCall = footerAsset ? getVersionHash(footerAsset) : null;

      // Extract topbar config from response
      let topbarPath = null;
      let topbarData = null;

      if (topbarResponse?.data?.data?.[0]) {
        const topbarAsset = topbarResponse.data.data[0];
        topbarPath = topbarAsset?.attributes?.assetPath;
        topbarData = topbarAsset?.attributes?.data;
      }

      // OPTIMIZATION: Only process topbar if we have valid data
      if (topbarPath && topbarData && configAssets?.data?.data) {
        // Replace or add topbar config to the configAssets response
        configAssets.data.data = configAssets.data.data.map(asset => {
          if (asset.attributes.assetPath === topbarPath) {
            // Replace existing topbar asset
            return {
              ...asset,
              attributes: {
                ...asset.attributes,
                data: topbarData,
              },
            };
          }
          return asset;
        });

        // If not found in existing config assets, push it in
        const exists = configAssets.data.data.some(asset => asset.attributes.assetPath === topbarPath);
        if (!exists) {
          configAssets.data.data.push({
            id: topbarResponse.data.data[0].id,
            type: topbarResponse.data.data[0].type,
            attributes: {
              assetPath: topbarPath,
              data: topbarData,
            },
          });
        }
      }

      const versionInConfigsCall = getVersionHash(configAssets);

      // OPTIMIZATION: Only check versions if we have footer data
      const hasSameVersions = footerAsset ? (
        versionInTranslationsCall === versionInFooterCall &&
        versionInFooterCall === versionInConfigsCall
      ) : (
        versionInTranslationsCall === versionInConfigsCall
      );

      // NOTE: making separate calls means that there might be version mismatch
      // when using 'latest' alias.
      // Since we only fetch translations and footer as a separate calls from configs,
      // there should not be major problems with this approach.
      // TODO: potentially show an error page or reload if version mismatch is detected.
      if (!version && !hasSameVersions) {
        console.warn("Asset versions between calls don't match.");
      }

      const googleAnalyticsId = getGoogleAnalyticsId(configAssets, assets.analytics);
      dispatch(appAssetsSuccess(assets, versionInTranslationsCall, googleAnalyticsId));

      // Returned value looks like this for a single asset with name: "translations":
      // {
      //    translations: {
      //      path: 'content/translations.json', // an example path in Asset Delivery API
      //      data, // translation key & value pairs
      //    },
      // }
      return assetEntries.reduce((collectedAssets, assetEntry, i) => {
        const [name, path] = assetEntry;

        if (nonConfigAssets.includes(name)) {
          // There are distinct calls for these assets
          const assetResponse = name === 'translations' ? translationAsset : footerAsset;
          // OPTIMIZATION: Handle missing assets gracefully
          if (assetResponse) {
            return { ...collectedAssets, [name]: { path, data: getFirstAssetData(assetResponse) } };
          } else {
            return { ...collectedAssets, [name]: { path, data: {} } };
          }
        }

        // Other asset path are assumed to be config assets
        const fetchedConfigAssets = getMultiAssetData(configAssets);
        const jsonAsset = findJSONAsset(fetchedConfigAssets, getAbsolutePath(path));

        // branding.json config asset can contain image references,
        // which should be denormalized from "included" section of the response
        const data = denormalizeAssetData({
          data: jsonAsset?.attributes?.data,
          included: getMultiAssetIncluded(configAssets),
        });
        return { ...collectedAssets, [name]: { path, data } };
      }, {});
    })
    .catch(e => {
      log.error(e, 'app-asset-fetch-failed', { assets, version });
      dispatch(appAssetsError(storableError(e)));
      // OPTIMIZATION: Return empty assets instead of throwing
      return assetEntries.reduce((collectedAssets, [name, path]) => {
        return { ...collectedAssets, [name]: { path, data: {} } };
      }, {});
    });
};

export const fetchPageAssets = (assets, hasFallback) => async (dispatch, getState, sdk) => {
  const version = getState()?.hostedAssets?.version;

  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';
  // Extract locale from URL (e.g., /fr/page), default to browser preference
  const fromUrl = SUPPORTED_LANGUAGES.find(
    code => code !== 'en' && (locationPath.startsWith(`/${code}/`) || locationPath === `/${code}`)
  );

  const appLanguage = fromUrl || 'en';

  if (typeof version === 'undefined') {
    throw new Error(
      'App-wide assets were not fetched first. Asset version missing from Redux store.'
    );
  }

  dispatch(pageAssetsRequested(Object.keys(assets)));

  // If version is given fetch assets by the version,
  // otherwise default to "latest" alias
  const fetchAssets = version
    ? assetPath => sdk.assetByVersion({ path: assetPath, version })
    : assetPath => sdk.assetByAlias({ path: assetPath, alias: 'latest' });

  // Mongo Translatio
  const keys = Object.keys(assets);

  // Mongo DB asset fetches
  const mongoAssetPromises = keys.map(key => {
    const pageIdMap = {
      landingPage: 'landing-page',
      termsOfService: 'terms-of-service',
      privacyPolicy: 'privacy-policy',
    };
    const pageId = pageIdMap[key] || key;
    return getPageDatabyIdandLanguage(pageId, appLanguage);
  });

  // SDK asset fetches
  const assetEntries = Object.entries(assets);
  // const sdkAssetPromises = assetEntries.map(([_, path]) => fetchAssets(path));

  // Fetch Mongo-translated pages
  return Promise.allSettled(mongoAssetPromises)
    .then(async mongoResults => {
      const hostedAssetsState = getState()?.hostedAssets;
      // These are fixed page assets that the app expects to be there. Keep fixed assets always in store.
      const { termsOfService, privacyPolicy, landingPage, ...rest } =
        hostedAssetsState?.pageAssetsData || {};
      const fixedPageAssets = { termsOfService, privacyPolicy, landingPage };

      // Avoid race condition, which might happen if automatic redirections try to fetch different assets
      // This could happen, when logged-in user clicks some signup link (AuthenticationPage fetches terms&privacy, LandingPage fetches its asset)
      const pickLatestPageAssetData = hostedAssetsState?.currentPageAssets.reduce(
        (collected, pa) => {
          const cmsPageData = rest[pa];
          return cmsPageData ? { ...collected, [pa]: cmsPageData } : collected;
        },
        {}
      );

      // Prepare pageAssets by reducing over successful results
      const pageAssets = assetEntries.reduce((collected, [name, path], i) => {
        const result = mongoResults[i];
        if (result.status === 'fulfilled') {
          const denormalizedAssetData = denormalizeAssetData(result.value.data.data);
          const assetData = sanitizePageAssetData(denormalizedAssetData);
          return { ...collected, [name]: { path, data: assetData } };
        } else {
          console.warn(`❌ Failed to fetch asset: ${name}`, result.reason);
          return collected;
        }
      }, { ...fixedPageAssets, ...pickLatestPageAssetData });

      dispatch(pageAssetsSuccess(pageAssets));
      return pageAssets;
    })
    .catch(e => {
      // If there's a fallback UI, something went wrong when fetching the "known asset" like landing-page.json.
      // If there's no fallback UI created, we assume that the page URL was mistyped for 404 errors.
      if (hasFallback || (!hasFallback && e.status === 404)) {
        log.error(e, 'page-asset-fetch-failed', { assets, version });
      }
      dispatch(pageAssetsError(storableError(e)));
    });
};
